import { useQuery } from "@tanstack/react-query";
import { supabaseClient } from "@/lib/supabase";

/** Public Better Stack status page - used as a client fallback (CORS allows *). */
const STATUS_PAGE_JSON_URL = "https://status.patient.watch/index.json";
const STATUS_PAGE_URL = "https://status.patient.watch/";

export type ResourceStatus =
    | "operational"
    | "degraded_performance"
    | "partial_outage"
    | "major_outage"
    | "maintenance"
    | "downtime"
    | "paused"
    | "pending"
    | "up"
    | "validating"
    | "down"
    | string;

export interface MonitorStatus {
    id: string;
    name: string;
    status: ResourceStatus;
    section?: string;
    lastChecked: string | null;
}

export interface StatusPageData {
    hasIssues: boolean;
    aggregateState?: string;
    statusPageUrl?: string;
    monitors: MonitorStatus[];
    description?: string;
}

function isIssueStatus(status: string): boolean {
    return (
        status === "downtime" ||
        status === "degraded" ||
        status === "degraded_performance" ||
        status === "partial_outage" ||
        status === "major_outage" ||
        status === "down" ||
        status === "validating"
    );
}

interface StatusPageJson {
    data: {
        id: string;
        attributes: {
            aggregate_state: string;
        };
    };
    included?: Array<{
        id: string;
        type: string;
        attributes: {
            name?: string;
            public_name?: string;
            status?: string;
            status_page_section_id?: number;
        };
    }>;
}

function mapStatusPageJson(page: StatusPageJson): StatusPageData {
    const aggregateState = page.data.attributes.aggregate_state ?? "operational";
    const included = page.included ?? [];

    const sections = new Map<string, string>();
    for (const item of included) {
        if (item.type === "status_page_section" && item.attributes.name) {
            sections.set(item.id, item.attributes.name);
        }
    }

    const monitors: MonitorStatus[] = included
        .filter((item) => item.type === "status_page_resource")
        .map((resource) => {
            const sectionId = String(resource.attributes.status_page_section_id ?? "");
            return {
                id: resource.id,
                name: resource.attributes.public_name || "Service",
                status: resource.attributes.status || "operational",
                section: sections.get(sectionId),
                lastChecked: null,
            };
        });

    const issueResources = monitors.filter((m) => isIssueStatus(m.status));
    const hasIssues = isIssueStatus(aggregateState) || aggregateState === "maintenance" || issueResources.length > 0;

    let description: string | undefined;
    if (hasIssues) {
        if (aggregateState === "maintenance") {
            description = "Scheduled maintenance in progress";
        } else if (issueResources.length > 0) {
            description = `Ongoing issues detected: ${issueResources.map((m) => m.name).join(", ")}`;
        } else {
            description = "Ongoing issue detected";
        }
    }

    return {
        hasIssues,
        aggregateState,
        statusPageUrl: STATUS_PAGE_URL,
        monitors,
        description,
    };
}

async function fetchFromStatusPageJson(): Promise<StatusPageData | null> {
    try {
        const response = await fetch(STATUS_PAGE_JSON_URL, {
            headers: { Accept: "application/json" },
        });
        if (!response.ok) return null;
        const page = (await response.json()) as StatusPageJson;
        if (!page?.data?.attributes) return null;
        return mapStatusPageJson(page);
    } catch {
        return null;
    }
}

async function fetchFromEdgeFunction(): Promise<StatusPageData | null> {
    try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        if (!supabaseUrl) return null;

        const { data, error } = await supabaseClient.functions.invoke("public-status-page");
        if (error) return null;

        if (data && typeof data === "object" && "hasIssues" in data) {
            return data as StatusPageData;
        }
        return null;
    } catch {
        return null;
    }
}

async function fetchStatusData(): Promise<StatusPageData | null> {
    // Prefer the public status page JSON (matches status.patient.watch, no secrets).
    const fromPage = await fetchFromStatusPageJson();
    if (fromPage) return fromPage;

    // Fallback to edge proxy if the public page is unreachable.
    return fetchFromEdgeFunction();
}

export function useStatusQuery() {
    return useQuery({
        queryKey: ["status-page"],
        queryFn: fetchStatusData,
        staleTime: 1000 * 60, // 1 minute
        refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
        retry: 1,
        enabled: typeof window !== "undefined",
    });
}
